import React, { useState, useRef, useMemo, useEffect } from 'react'
import { personalOfferContainer } from 'src/models/PersonalOffer/PersonalOffer'
import {
  DesktopIcon,
  TabletIcon,
  MobileIcon,
  CloseIcon,
} from '../MysitePreviewHeader/MysitePreviewHeaderIcons'
import {
  DEVICE_TYPES,
  detectDeviceType,
  generatePreviewIframeUrl,
  getShopDisplayName,
  scrollIframeContent,
  isInsidePreviewIframe,
} from './personalOfferPreviewHeaderUtils'
import { Loader } from 'src/views/components/Loader'
import { IS_BROWSER } from 'config/appConfig'
import { getLocaleCodeFromUrl } from 'src/utils'
import { enablePORedesignFeature } from 'src/utils/commonUtils'
import { checkIsNativeApp } from 'src/utils/reactNativeAppUtils'
import './styles.scss'
const PersonalOfferPreviewHeader = () => {
  const [selectedDevice, setSelectedDevice] = useState(detectDeviceType)
  const [isLoading, setIsLoading] = useState(true)
  const [csTranslations, setCsTranslations] = useState(null)
  const previewIframeRef = useRef(null)
  const previewMaskRef = useRef(null)
  const currentLocale = getLocaleCodeFromUrl({
    defaultLocale: 'en_US',
    isReverseType: true,
  })
  // Get translations from nextgen_productoffer ContentStack content type
  const translations = useMemo(() => {
    const csValues = csTranslations || {}
    return {
      preview: csValues?.labelPreview || 'Preview',
      title: csValues?.labelTitle || 'Personal Offer',
      closeWindow: csValues?.labelCloseWindow || 'Close Window',
      desktopView: csValues?.labelDesktopView || 'Desktop View',
      tabletView: csValues?.labelTabletView || 'Tablet View',
      mobileView: csValues?.labelMobileView || 'Mobile View',
      deviceSelection:
        csValues?.labelDeviceSelection || 'Device preview selection',
    }
  }, [csTranslations])
  const shopDisplayName = useMemo(() => {
    const poSiteResponse = personalOfferContainer?.poSiteShopHeaderResponse
    const isProductOfferV2Enabled = enablePORedesignFeature()
    const poData = personalOfferContainer?.personalOfferData
    // For V2, prioritize offer name from personalOfferData
    if (isProductOfferV2Enabled && poData?.name) {
      return poData.name
    }
    return getShopDisplayName(poSiteResponse, currentLocale)
  }, [currentLocale])
  useEffect(() => {
    // Skip setup if inside the preview iframe
    if (isInsidePreviewIframe()) return

    if (IS_BROWSER) {
      const initialDeviceType = detectDeviceType()
      setSelectedDevice(initialDeviceType)
      document.body.classList.add('po-preview')
      document.body.classList.add('no-scroll')
      // Load translations from nextgen_productoffer ContentStack
      const loadTranslations = async () => {
        try {
          if (!personalOfferContainer?.productOffersCSValues) {
            await personalOfferContainer.getProductOffersCSValues()
          }
          setCsTranslations(personalOfferContainer?.productOffersCSValues)
        } catch (error) {
          console.error('Error loading PO preview translations:', error)
        }
      }
      loadTranslations()
    }

    // Attach wheel listener with { passive: false } to allow preventDefault
    const maskEl = previewMaskRef.current
    const handleMaskScroll = event => {
      scrollIframeContent(previewIframeRef.current, event)
    }
    if (maskEl) {
      maskEl.addEventListener('wheel', handleMaskScroll, { passive: false })
    }
    return () => {
      if (IS_BROWSER) {
        document.body.classList.remove('po-preview')
        document.body.classList.remove('no-scroll')
      }
      if (maskEl) {
        maskEl.removeEventListener('wheel', handleMaskScroll)
      }
    }
  }, [])
  // Early return must be AFTER all hooks
  if (isInsidePreviewIframe()) {
    return null
  }
  const getIframeUrl = () => {
    const urlParams = new URLSearchParams(window?.location?.search)
    const pitchId = urlParams.get('pitchId')
    return generatePreviewIframeUrl({ pitchId })
  }
  const handleDeviceSelection = deviceType => {
    setSelectedDevice(deviceType)
  }
  const handleIframeLoad = () => {
    setTimeout(() => setIsLoading(false), 2000)
    try {
      const iframeDoc =
        previewIframeRef.current?.contentDocument ||
        previewIframeRef.current?.contentWindow?.document
      if (iframeDoc) {
        const style = iframeDoc.createElement('style')
        style.textContent = `
          * {
            cursor: default !important;
          }
          a, button, [role="button"], input[type="submit"], select {
            pointer-events: none !important;
          }
        `
        iframeDoc.head.appendChild(style)
        // Block all click events inside the preview iframe
        iframeDoc.addEventListener(
          'click',
          e => {
            e.preventDefault()
            e.stopPropagation()
          },
          true
        )
      }
    } catch (e) {
      console.error(
        'Preview iframe cross-origin, using mask fallback:',
        e.message
      )
    }
  }
  const deviceConfig = useMemo(
    () => [
      {
        type: DEVICE_TYPES.DESKTOP,
        Icon: DesktopIcon,
        title: translations.desktopView,
      },
      {
        type: DEVICE_TYPES.TABLET,
        Icon: TabletIcon,
        title: translations.tabletView,
      },
      {
        type: DEVICE_TYPES.MOBILE,
        Icon: MobileIcon,
        title: translations.mobileView,
      },
    ],
    [translations]
  )
  const handleClosePreview = () => {
    IS_BROWSER && window?.close()
  }
  const handleKeyDown = (event, callback) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      callback()
    }
  }

  const isNativeAppQueryParam =
    IS_BROWSER &&
    new URLSearchParams(window?.location?.search || '').get('isNativeApp') ===
      'true'
  const isNativeAppPreview = isNativeAppQueryParam || checkIsNativeApp()

  const renderDeviceButton = (deviceType, Icon, title) => {
    const isSelected = selectedDevice === deviceType
    return (
      <button
        key={deviceType}
        className={`po-header-preview__device po-header-preview__device--${deviceType} ${
          isSelected ? 'active' : ''
        }`}
        onClick={() => handleDeviceSelection(deviceType)}
        onKeyDown={e =>
          handleKeyDown(e, () => handleDeviceSelection(deviceType))
        }
        title={title}
        type="button"
        tabIndex={0}
        aria-label={title}
        aria-pressed={isSelected}
        role="button">
        <span aria-hidden="true">
          <Icon className="po-device-icon" />
        </span>
      </button>
    )
  }
  return (
    <div className="po-preview-wrapper">
      <div className="po-header-preview">
        <div className="po-header-preview__container">
          <div className="po-header-preview__content">
            {shopDisplayName && (
              <span className="po-header-preview__title" tabIndex={0}>
                {shopDisplayName} - {translations.preview}
              </span>
            )}
            {!shopDisplayName && (
              <span className="po-header-preview__title" tabIndex={0}>
                {translations.title} - {translations.preview}
              </span>
            )}
          </div>
          <div
            className="po-header-preview__devices"
            role="toolbar"
            aria-label={translations.deviceSelection}>
            {deviceConfig.map(({ type, Icon, title }) =>
              renderDeviceButton(type, Icon, title)
            )}
          </div>
          <button
            className="po-header-preview__close"
            onClick={handleClosePreview}
            onKeyDown={e => handleKeyDown(e, handleClosePreview)}
            type="button"
            tabIndex={0}
            aria-label={translations.closeWindow}>
            {!isNativeAppPreview && (
              <span aria-hidden="true">
                <CloseIcon className="po-close-icon" />
              </span>
            )}
            <span className="po-header-preview__close-text">
              {translations.closeWindow}
            </span>
          </button>
        </div>
      </div>
      <div className={`po-preview-container ${selectedDevice}-view`}>
        {isLoading && (
          <div className="po-preview-loading-overlay">
            <Loader />
          </div>
        )}
        <iframe
          ref={previewIframeRef}
          src={getIframeUrl()}
          className={`po-preview-iframe po-preview-iframe--${selectedDevice}`}
          title={`${shopDisplayName || 'Personal Offer'} Preview - ${selectedDevice} view`}
          frameBorder="0"
          sandbox="allow-same-origin allow-scripts"
          onLoad={handleIframeLoad}
          style={isLoading ? { opacity: 0, position: 'absolute' } : {}}
        />
        <div className="po-preview-mask" ref={previewMaskRef} />
      </div>
    </div>
  )
}
export default PersonalOfferPreviewHeader
